import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Truck, Clock, Phone, CheckCircle, ArrowRight, Star, Wrench, Tent, Leaf, Sparkles, ChevronRight } from 'lucide-react';
import QuickQuoteWidget from '../components/QuickQuoteWidget';
import {useSeo} from '../seo/SeoProvider';

// Données des villes
const citiesData = {
  'le-havre': {
    name: 'Le Havre',
    region: 'Seine-Maritime',
    distance: '15 minutes',
    population: '170 000 habitants',
    description: 'Première ville de Normandie, Le Havre est au cœur de notre zone de livraison. Que vous organisiez un événement au bord de mer, des travaux de rénovation ou l\'entretien de votre jardin, 3DLoc vous accompagne.',
    metaTitle: 'Location de Matériel au Havre | Chapiteaux, Outillage, Jardinage | 3DLoc',
    metaDescription: 'Location de matériel au Havre : chapiteaux, barnums, outillage, jardinage. Livraison rapide en 24h. +300 références. Devis gratuit. 3DLoc, votre partenaire local.',
    highlights: [
      'Livraison express sous 24h',
      'Retrait gratuit à Cauville-sur-Mer (15 min)',
      'Service 7J/7 sur rendez-vous',
      '+300 références disponibles'
    ],
    popularServices: [
      { name: 'Location barnum mariage', icon: Tent },
      { name: 'Location bétonnière', icon: Wrench },
      { name: 'Location structure gonflable', icon: Tent },
      { name: 'Location motoculteur', icon: Leaf }
    ],
    localContent: 'Le Havre, ville classée au patrimoine mondial de l\'UNESCO, accueille chaque année de nombreux événements : mariages en bord de mer, festivals, séminaires d\'entreprise. 3DLoc est le partenaire idéal pour équiper vos événements avec du matériel de qualité professionnelle.',
    nearbyAreas: ['Sainte-Adresse', 'Harfleur', 'Montivilliers', 'Gonfreville-l\'Orcher']
  },
  'rouen': {
    name: 'Rouen',
    region: 'Seine-Maritime',
    distance: '1 heure',
    population: '115 000 habitants',
    description: 'Capitale historique de la Normandie, Rouen bénéficie de notre service de livraison étendu. Pour vos événements dans la métropole rouennaise ou vos projets de bricolage, nous sommes à votre service.',
    metaTitle: 'Location de Matériel à Rouen | Événementiel, Bricolage | 3DLoc',
    metaDescription: 'Location de matériel à Rouen et métropole : chapiteaux, mobilier événementiel, outillage. Livraison sous 48h. Devis gratuit. 3DLoc Normandie.',
    highlights: [
      'Livraison sous 48-72h',
      'Couverture métropole rouennaise',
      'Matériel professionnel',
      'Tarifs compétitifs'
    ],
    popularServices: [
      { name: 'Location chapiteau événement', icon: Tent },
      { name: 'Location sono', icon: Sparkles },
      { name: 'Location échafaudage', icon: Wrench },
      { name: 'Location broyeur végétaux', icon: Leaf }
    ],
    localContent: 'Rouen, avec son centre historique et ses quais de Seine, est une ville prisée pour les événements. De l\'Armada aux marchés de Noël, en passant par les mariages dans les châteaux environnants, 3DLoc équipe vos projets avec du matériel fiable et professionnel.',
    nearbyAreas: ['Sotteville-lès-Rouen', 'Mont-Saint-Aignan', 'Petit-Quevilly', 'Bois-Guillaume']
  },
  'fecamp': {
    name: 'Fécamp',
    region: 'Seine-Maritime',
    distance: '25 minutes',
    population: '18 000 habitants',
    description: 'Port de pêche historique et station balnéaire, Fécamp est idéalement située dans notre zone prioritaire. Profitez d\'une livraison rapide pour tous vos projets.',
    metaTitle: 'Location de Matériel à Fécamp | Livraison Rapide | 3DLoc',
    metaDescription: 'Location de matériel à Fécamp : outillage, jardinage, événementiel. Livraison sous 24h depuis Cauville-sur-Mer. 3DLoc, votre loueur local en Normandie.',
    highlights: [
      'Livraison rapide sous 24h',
      'À 25 min de notre dépôt',
      'Idéal événements bord de mer',
      'Matériel adapté au climat normand'
    ],
    popularServices: [
      { name: 'Location barnum plage', icon: Tent },
      { name: 'Location nettoyeur haute pression', icon: Sparkles },
      { name: 'Location taille-haie', icon: Leaf },
      { name: 'Location groupe électrogène', icon: Wrench }
    ],
    localContent: 'Fécamp, connue pour son Palais Bénédictine et ses falaises spectaculaires, accueille de nombreux événements estivaux. Pour vos fêtes de famille, kermesses ou travaux de rénovation, 3DLoc livre rapidement tout le matériel nécessaire.',
    nearbyAreas: ['Yport', 'Étretat', 'Saint-Léonard', 'Valmont']
  },
  'etretat': {
    name: 'Étretat',
    region: 'Seine-Maritime',
    distance: '15 minutes',
    population: '1 200 habitants',
    description: 'Joyau de la Côte d\'Albâtre, Étretat est à seulement 15 minutes de notre dépôt. Nous équipons les nombreux événements et mariages qui se déroulent face aux célèbres falaises.',
    metaTitle: 'Location de Matériel à Étretat | Mariages & Événements | 3DLoc',
    metaDescription: 'Location de matériel à Étretat pour mariages et événements face aux falaises. Chapiteaux, mobilier, décoration. Livraison express. 3DLoc Normandie.',
    highlights: [
      'Livraison express 15 min',
      'Spécialiste mariages vue mer',
      'Matériel résistant au vent',
      'Accompagnement personnalisé'
    ],
    popularServices: [
      { name: 'Location chapiteau mariage', icon: Tent },
      { name: 'Location mobilier cérémonie', icon: Tent },
      { name: 'Location chauffage extérieur', icon: Sparkles },
      { name: 'Location éclairage déco', icon: Sparkles }
    ],
    localContent: 'Étretat, avec ses falaises mondialement connues et ses jardins suspendus, est l\'une des destinations les plus prisées pour les mariages en Normandie. 3DLoc accompagne chaque année des dizaines de couples pour créer l\'événement parfait face à l\'Aiguille Creuse.',
    nearbyAreas: ['Bénouville', 'Le Tilleul', 'Criquebeuf-en-Caux', 'Bordeaux-Saint-Clair']
  },
  'honfleur': {
    name: 'Honfleur',
    region: 'Calvados',
    distance: '35 minutes',
    population: '6 500 habitants',
    description: 'Cité des peintres et perle du Calvados, Honfleur attire des visiteurs du monde entier. 3DLoc dessert cette ville pittoresque pour tous vos événements et projets.',
    metaTitle: 'Location de Matériel à Honfleur | Événements Calvados | 3DLoc',
    metaDescription: 'Location de matériel à Honfleur et Calvados : chapiteaux, mobilier, outillage. Livraison depuis Le Havre. Mariages, événements. 3DLoc Normandie.',
    highlights: [
      'Livraison sous 48h',
      'Couverture Pays d\'Auge',
      'Expertise événementiel haut de gamme',
      'Partenaire mariages de luxe'
    ],
    popularServices: [
      { name: 'Location tente de réception', icon: Tent },
      { name: 'Location vaisselle', icon: Sparkles },
      { name: 'Location sono DJ', icon: Sparkles },
      { name: 'Location débroussailleuse', icon: Leaf }
    ],
    localContent: 'Honfleur, avec son Vieux Bassin et ses maisons à colombages, est un écrin parfait pour les événements d\'exception. Manoirs, jardins privés et quais pittoresques accueillent mariages et réceptions. 3DLoc fournit le matériel qui sublimera votre événement.',
    nearbyAreas: ['Deauville', 'Trouville-sur-Mer', 'Villerville', 'Pennedepie']
  },
  'dieppe': {
    name: 'Dieppe',
    region: 'Seine-Maritime',
    distance: '1h15',
    population: '28 000 habitants',
    description: 'Station balnéaire historique de la Côte d\'Albâtre, Dieppe accueille de nombreux événements tout au long de l\'année. 3DLoc livre votre matériel directement sur place pour vos projets.',
    metaTitle: 'Location de Matériel à Dieppe | Événementiel, Bricolage | 3DLoc',
    metaDescription: 'Location de matériel à Dieppe : chapiteaux, barnums, outillage, jardinage. Livraison en Seine-Maritime. Devis gratuit. 3DLoc, votre partenaire en Normandie.',
    highlights: [
      'Livraison sous 48-72h',
      'Couverture Côte d\'Albâtre',
      'Matériel résistant aux embruns',
      'Spécialiste événements bord de mer'
    ],
    popularServices: [
      { name: 'Location barnum plage', icon: Tent },
      { name: 'Location mobilier événement', icon: Tent },
      { name: 'Location nettoyeur haute pression', icon: Sparkles },
      { name: 'Location groupe électrogène', icon: Wrench }
    ],
    localContent: 'Dieppe, avec son port pittoresque, ses falaises et sa Foire aux Harengs, est une ville festive par excellence. Entre le Festival international de cerf-volant et les nombreux mariages organisés sur la Côte d\'Albâtre, 3DLoc équipe tous vos projets avec du matériel fiable et adapté au climat maritime.',
    nearbyAreas: ['Pourville-sur-Mer', 'Arques-la-Bataille', 'Saint-Aubin-sur-Scie', 'Offranville']
  },
  'caen': {
    name: 'Caen',
    region: 'Calvados',
    distance: '1h15',
    population: '106 000 habitants',
    description: 'Préfecture du Calvados et capitale de la Basse-Normandie, Caen est une ville dynamique qui accueille étudiants, entreprises et touristes. 3DLoc dessert toute l\'agglomération caennaise.',
    metaTitle: 'Location de Matériel à Caen | Événementiel, Travaux | 3DLoc Calvados',
    metaDescription: 'Location de matériel à Caen et métropole : chapiteaux, mobilier événementiel, outillage professionnel. Livraison en Calvados. Devis gratuit. 3DLoc Normandie.',
    highlights: [
      'Livraison sous 48-72h',
      'Couverture agglomération caennaise',
      'Matériel professionnel et événementiel',
      'Tarifs compétitifs entreprises'
    ],
    popularServices: [
      { name: 'Location chapiteau réception', icon: Tent },
      { name: 'Location sono et éclairage', icon: Sparkles },
      { name: 'Location échafaudage', icon: Wrench },
      { name: 'Location broyeur végétaux', icon: Leaf }
    ],
    localContent: 'Caen, ville chargée d\'histoire avec son château ducal et ses abbayes, est un centre économique majeur de la Normandie. Entreprises, collectivités et particuliers comptent sur 3DLoc pour leurs événements professionnels, mariages et travaux. Notre service de livraison rapide couvre toute la métropole.',
    nearbyAreas: ['Hérouville-Saint-Clair', 'Mondeville', 'Ifs', 'Bretteville-sur-Odon']
  },
  'deauville': {
    name: 'Deauville',
    region: 'Calvados',
    distance: '45 minutes',
    population: '3 500 habitants',
    description: 'Station balnéaire de prestige de la Côte Fleurie, Deauville est synonyme d\'élégance et d\'événements haut de gamme. 3DLoc fournit du matériel à la hauteur de vos réceptions.',
    metaTitle: 'Location de Matériel à Deauville | Mariages Haut de Gamme | 3DLoc',
    metaDescription: 'Location de matériel à Deauville pour mariages de prestige et événements d\'exception. Chapiteaux premium, mobilier élégant. Livraison Côte Fleurie. 3DLoc.',
    highlights: [
      'Livraison sous 48h',
      'Matériel événementiel haut de gamme',
      'Spécialiste mariages prestige',
      'Accompagnement personnalisé'
    ],
    popularServices: [
      { name: 'Location chapiteau prestige', icon: Tent },
      { name: 'Location mobilier élégant', icon: Tent },
      { name: 'Location sono DJ professionnelle', icon: Sparkles },
      { name: 'Location chauffage terrasse', icon: Sparkles }
    ],
    localContent: 'Deauville, capitale du septième art avec son Festival du Film Américain et ses Planches mythiques, est le décor rêvé pour des événements d\'exception. Villas, hôtels de luxe et propriétés privées font appel à 3DLoc pour équiper mariages, galas et réceptions sur la Côte Fleurie.',
    nearbyAreas: ['Trouville-sur-Mer', 'Villers-sur-Mer', 'Touques', 'Saint-Arnoult']
  },
  'lisieux': {
    name: 'Lisieux',
    region: 'Calvados',
    distance: '1h',
    population: '20 000 habitants',
    description: 'Capitale du Pays d\'Auge, Lisieux est une destination touristique et spirituelle majeure. 3DLoc livre votre matériel dans toute la région pour vos événements et travaux.',
    metaTitle: 'Location de Matériel à Lisieux | Pays d\'Auge, Calvados | 3DLoc',
    metaDescription: 'Location de matériel à Lisieux et Pays d\'Auge : chapiteaux, mobilier, outillage, jardinage. Livraison en Calvados. Devis gratuit. 3DLoc Normandie.',
    highlights: [
      'Livraison sous 48-72h',
      'Couverture Pays d\'Auge',
      'Matériel adapté au terroir normand',
      'Service 7J/7 sur rendez-vous'
    ],
    popularServices: [
      { name: 'Location barnum champêtre', icon: Tent },
      { name: 'Location mobilier rustique', icon: Tent },
      { name: 'Location motoculteur', icon: Leaf },
      { name: 'Location tronçonneuse', icon: Wrench }
    ],
    localContent: 'Lisieux, avec sa célèbre Basilique Sainte-Thérèse et son patrimoine normand authentique, attire chaque année de nombreux visiteurs. Mariages dans les manoirs du Pays d\'Auge, fêtes de village, travaux agricoles et de jardinage : 3DLoc accompagne tous vos projets avec un service fiable.',
    nearbyAreas: ['Saint-Désir', 'Glos', 'Beuvillers', 'Ouilly-le-Vicomte']
  },
  'evreux': {
    name: 'Évreux',
    region: 'Eure',
    distance: '1h30',
    population: '47 000 habitants',
    description: 'Préfecture de l\'Eure et porte d\'entrée de la Normandie depuis Paris, Évreux bénéficie de notre service de livraison étendu. 3DLoc équipe vos projets en toute région.',
    metaTitle: 'Location de Matériel à Évreux | Eure, Normandie | 3DLoc',
    metaDescription: 'Location de matériel à Évreux : chapiteaux, mobilier événementiel, outillage. Livraison dans l\'Eure. Devis gratuit. 3DLoc, votre loueur en Normandie.',
    highlights: [
      'Livraison sur demande',
      'Couverture département de l\'Eure',
      'Matériel professionnel et événementiel',
      'Devis rapide sous 24h'
    ],
    popularServices: [
      { name: 'Location chapiteau événement', icon: Tent },
      { name: 'Location mobilier cérémonie', icon: Tent },
      { name: 'Location bétonnière', icon: Wrench },
      { name: 'Location broyeur végétaux', icon: Leaf }
    ],
    localContent: 'Évreux, cité épiscopale nichée dans la vallée de l\'Iton, est un pôle économique et culturel dynamique de la Normandie. Entre la cathédrale Notre-Dame et les nombreux événements locaux, 3DLoc accompagne particuliers, entreprises et collectivités pour leurs mariages, séminaires et travaux.',
    nearbyAreas: ['Guichainville', 'Saint-Sébastien-de-Morsent', 'Gravigny', 'Le Vieil-Évreux']
  },
  'montivilliers': {
    name: 'Montivilliers',
    region: 'Seine-Maritime',
    distance: '20 minutes',
    population: '16 000 habitants',
    description: 'Ville historique de l\'agglomération havraise, Montivilliers est au cœur de notre zone prioritaire. 3DLoc livre rapidement tout type de matériel pour vos événements et projets.',
    metaTitle: 'Location de Matériel à Montivilliers | Livraison 24h | 3DLoc',
    metaDescription: 'Location de matériel à Montivilliers : chapiteaux, outillage, jardinage. Livraison rapide depuis Cauville-sur-Mer (20 min). Devis gratuit. 3DLoc Normandie.',
    highlights: [
      'Livraison express sous 24h',
      'À 20 min de notre dépôt',
      'Matériel professionnel',
      'Service 7J/7 sur rendez-vous'
    ],
    popularServices: [
      { name: 'Location barnum réception', icon: Tent },
      { name: 'Location mobilier événement', icon: Tent },
      { name: 'Location bétonnière', icon: Wrench },
      { name: 'Location motoculteur', icon: Leaf }
    ],
    localContent: 'Montivilliers, avec son abbaye millénaire et son centre-ville dynamique, est une ville prisée pour les mariages et événements familiaux. 3DLoc accompagne chaque année de nombreux habitants de Montivilliers pour leurs fêtes, travaux et projets de jardinage.',
    nearbyAreas: ['Épouville', 'Rolleville', 'Fontaine-la-Mallet', 'Manéglise']
  },
  'harfleur': {
    name: 'Harfleur',
    region: 'Seine-Maritime',
    distance: '18 minutes',
    population: '8 000 habitants',
    description: 'Cité médiévale aux portes du Havre, Harfleur bénéficie de notre service de livraison prioritaire. Nous couvrons tous vos besoins en location de matériel.',
    metaTitle: 'Location de Matériel à Harfleur | Livraison Rapide | 3DLoc',
    metaDescription: 'Location de matériel à Harfleur : événementiel, bricolage, jardinage. Livraison sous 24h depuis Cauville-sur-Mer. Devis gratuit. 3DLoc, partenaire local.',
    highlights: [
      'Livraison express 24h',
      'À 18 min de notre dépôt',
      'Matériel adapté à tous les projets',
      'Tarifs compétitifs'
    ],
    popularServices: [
      { name: 'Location chapiteau mariage', icon: Tent },
      { name: 'Location échafaudage', icon: Wrench },
      { name: 'Location nettoyeur haute pression', icon: Sparkles },
      { name: 'Location broyeur végétaux', icon: Leaf }
    ],
    localContent: 'Harfleur, avec son patrimoine médiéval et sa proximité immédiate avec Le Havre, est une ville dynamique où 3DLoc intervient régulièrement. Mariages, fêtes de quartier, travaux de rénovation ou entretien de jardin : notre matériel est disponible rapidement.',
    nearbyAreas: ['Gonfreville-l\'Orcher', 'Le Havre', 'Gainneville', 'Rogerville']
  },
  'sainte-adresse': {
    name: 'Sainte-Adresse',
    region: 'Seine-Maritime',
    distance: '18 minutes',
    population: '7 500 habitants',
    description: 'Station balnéaire résidentielle aux portes du Havre, Sainte-Adresse offre un cadre d\'exception pour vos événements en bord de mer. 3DLoc équipe vos projets avec du matériel de qualité.',
    metaTitle: 'Location de Matériel à Sainte-Adresse | Bord de Mer | 3DLoc',
    metaDescription: 'Location de matériel à Sainte-Adresse : barnums, mobilier événementiel, outillage. Livraison express en Seine-Maritime. Devis gratuit. 3DLoc Normandie.',
    highlights: [
      'Livraison sous 24h',
      'Idéal événements vue mer',
      'Matériel résistant aux embruns',
      'Service premium'
    ],
    popularServices: [
      { name: 'Location barnum bord de mer', icon: Tent },
      { name: 'Location mobilier réception', icon: Tent },
      { name: 'Location chauffage extérieur', icon: Sparkles },
      { name: 'Location nettoyeur haute pression', icon: Sparkles }
    ],
    localContent: 'Sainte-Adresse, perchée sur les hauteurs du Cap de la Hève, offre des panoramas exceptionnels sur la baie de Seine. Villas privées, jardins avec vue mer et événements résidentiels : 3DLoc apporte le matériel qui sublimera vos réceptions dans ce cadre unique.',
    nearbyAreas: ['Le Havre', 'Octeville-sur-Mer', 'Cauville-sur-Mer', 'Fontaine-la-Mallet']
  },
  'bolbec': {
    name: 'Bolbec',
    region: 'Seine-Maritime',
    distance: '30 minutes',
    population: '11 000 habitants',
    description: 'Ville industrielle et commerçante de la vallée du Commerce, Bolbec est un carrefour régional. 3DLoc livre tout type de matériel pour vos besoins professionnels et particuliers.',
    metaTitle: 'Location de Matériel à Bolbec | Seine-Maritime | 3DLoc',
    metaDescription: 'Location de matériel à Bolbec : chapiteaux, outillage, mobilier événementiel. Livraison en Seine-Maritime. Devis gratuit. 3DLoc, votre partenaire en Normandie.',
    highlights: [
      'Livraison sous 48h',
      'Couverture vallée du Commerce',
      'Matériel professionnel',
      'Tarifs compétitifs'
    ],
    popularServices: [
      { name: 'Location barnum événement', icon: Tent },
      { name: 'Location bétonnière', icon: Wrench },
      { name: 'Location tronçonneuse', icon: Leaf },
      { name: 'Location nettoyeur haute pression', icon: Sparkles }
    ],
    localContent: 'Bolbec, capitale historique du textile normand, reste une ville active et dynamique. Comités d\'entreprise, particuliers pour mariages et fêtes familiales, artisans pour leurs chantiers : 3DLoc équipe tous types de projets dans la vallée du Commerce.',
    nearbyAreas: ['Gruchet-le-Valasse', 'Beuzeville-la-Grenier', 'Lillebonne', 'Goderville']
  },
  'lillebonne': {
    name: 'Lillebonne',
    region: 'Seine-Maritime',
    distance: '35 minutes',
    population: '9 000 habitants',
    description: 'Ville historique aux vestiges gallo-romains, Lillebonne est un pôle économique de l\'estuaire. 3DLoc livre votre matériel dans toute la vallée de la Seine.',
    metaTitle: 'Location de Matériel à Lillebonne | Vallée de Seine | 3DLoc',
    metaDescription: 'Location de matériel à Lillebonne : barnums, outillage, jardinage. Livraison depuis Cauville-sur-Mer. Devis gratuit. 3DLoc, partenaire location en Normandie.',
    highlights: [
      'Livraison sous 48h',
      'Couverture vallée de Seine',
      'Matériel adapté événements',
      'Service professionnel'
    ],
    popularServices: [
      { name: 'Location chapiteau réception', icon: Tent },
      { name: 'Location mobilier événement', icon: Tent },
      { name: 'Location échafaudage', icon: Wrench },
      { name: 'Location motoculteur', icon: Leaf }
    ],
    localContent: 'Lillebonne, avec son théâtre antique romain Juliobona et son patrimoine remarquable, accueille chaque année de nombreux événements culturels et familiaux. Proche de la zone industrielle de Port-Jérôme, 3DLoc sert aussi bien les particuliers que les entreprises de la région.',
    nearbyAreas: ['Port-Jérôme-sur-Seine', 'Tancarville', 'Petiville', 'Gruchet-le-Valasse']
  },
  'pont-audemer': {
    name: 'Pont-Audemer',
    region: 'Eure',
    distance: '40 minutes',
    population: '8 500 habitants',
    description: 'Surnommée la "Petite Venise Normande", Pont-Audemer offre un cadre pittoresque pour vos événements. 3DLoc dessert cette ville de l\'Eure et ses environs.',
    metaTitle: 'Location de Matériel à Pont-Audemer | Eure, Normandie | 3DLoc',
    metaDescription: 'Location de matériel à Pont-Audemer : chapiteaux, mobilier événementiel, outillage. Livraison dans l\'Eure. Devis gratuit. 3DLoc, votre loueur en Normandie.',
    highlights: [
      'Livraison sous 48h',
      'Couverture nord de l\'Eure',
      'Matériel événementiel varié',
      'Devis rapide'
    ],
    popularServices: [
      { name: 'Location barnum mariage', icon: Tent },
      { name: 'Location mobilier cérémonie', icon: Tent },
      { name: 'Location sono DJ', icon: Sparkles },
      { name: 'Location broyeur végétaux', icon: Leaf }
    ],
    localContent: 'Pont-Audemer, avec ses canaux, ses maisons à colombages et son Marais Vernier à proximité, est une destination prisée pour les mariages champêtres et les événements familiaux. 3DLoc équipe vos réceptions avec du matériel adapté à ce cadre bucolique unique.',
    nearbyAreas: ['Beuzeville', 'Berville-sur-Mer', 'Saint-Germain-Village', 'Toutainville']
  }
};

// FAQ générique par ville pour Schema.org
const cityIn = (name) => (name === 'Le Havre' ? 'au Havre' : `à ${name}`);
const cityOf = (name) => (name === 'Le Havre' ? 'du Havre' : `de ${name}`);
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const getCityFAQ = (cityData) => [
  {
    question: `3DLoc livre-t-il du matériel ${cityIn(cityData.name)} ?`,
    answer: `Oui, 3DLoc livre du matériel de location ${cityIn(cityData.name)} et ses environs. Notre dépôt à Cauville-sur-Mer est situé à ${cityData.distance} ${cityOf(cityData.name)}, ce qui nous permet d'assurer une livraison fiable dans toute la ${cityData.region}.`
  },
  {
    question: `Quels types de matériel puis-je louer ${cityIn(cityData.name)} ?`,
    answer: `${capitalize(cityIn(cityData.name))}, vous pouvez louer tout notre catalogue : chapiteaux, barnums, structures gonflables, mobilier événementiel, outillage de bricolage, matériel de jardinage, nettoyeurs haute pression et bien plus. Plus de 300 références disponibles.`
  },
  {
    question: `Comment obtenir un devis pour une location ${cityIn(cityData.name)} ?`,
    answer: `Pour obtenir un devis gratuit pour une location ${cityIn(cityData.name)}, contactez-nous par téléphone au 06 23 89 32 98 ou via notre formulaire de contact en ligne. Nous vous répondons sous 24h avec une proposition personnalisée.`
  },
  {
    question: `Quel est le délai de livraison ${cityIn(cityData.name)} ?`,
    answer: `Les délais de livraison ${cityIn(cityData.name)} sont généralement de 24h à 72h selon la distance et le matériel demandé. Pour les événements planifiés, nous recommandons de réserver au moins 2 semaines à l'avance.`
  }
];

// Composant universel pour les services
const ServiceCard = ({ service }) => {
  const Icon = service.icon;
  return (
    <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-green-400 hover:shadow-md transition-all">
      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
        <Icon size={20} className="text-green-600" />
      </div>
      <span className="font-medium text-slate-700">{service.name}</span>
    </div>
  );
};

const CityLandingPage = () => {
  const { city } = useParams();
  const cityData = citiesData[city];
  const {seo} = useSeo();

  // SEO: mise à jour du title, meta description et injection JSON-LD
  useEffect(() => {
    if (!cityData) return;


    const cityFAQ = getCityFAQ(cityData);
    const baseUrl = process.env.REACT_APP_SITE_URL;

    // LocalBusiness Schema
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${baseUrl}/location-materiel/${city}#business`,
      name: `3DLoc - Location de Matériel ${cityIn(cityData.name)}`,
      description: cityData.metaDescription,
      url: `${baseUrl}/location-materiel/${city}`,
      telephone: '+33623893298',
      priceRange: '€€',
      areaServed: {
        '@type': 'City',
        name: cityData.name,
        containedInPlace: {
          '@type': 'AdministrativeArea',
          name: cityData.region
        }
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '4 rue des Tilleuls',
        addressLocality: 'Cauville-sur-Mer',
        postalCode: '76930',
        addressCountry: 'FR'
      }
    };

    // BreadcrumbList Schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: baseUrl },
        { '@type': 'ListItem', position: 2, name: 'Zones de livraison', item: `${baseUrl}/zones-livraison` },
        { '@type': 'ListItem', position: 3, name: cityData.name, item: `${baseUrl}/location-materiel/${city}` }
      ]
    };

    // FAQPage Schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: cityFAQ.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    };

    const schemas = [localBusinessSchema, breadcrumbSchema, faqSchema];
    const scriptTags = schemas.map((schema, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `city-schema-${idx}`;
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });

    return () => {
      scriptTags.forEach(s => s.remove());
    };
  }, [city, cityData]);

  // Si la ville n'existe pas, rediriger vers la page zones
  if (!cityData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Ville non trouvée</h1>
          <Link to="/zones-livraison" className="text-green-600 hover:underline">
            Voir toutes nos zones de livraison
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Breadcrumb visible (SEO) */}
      <nav aria-label="Fil d'Ariane" className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center gap-2 text-sm text-slate-600">
            <li><Link to="/" className="hover:text-green-600">Accueil</Link></li>
            <li><ChevronRight size={14} className="text-slate-400" /></li>
            <li><Link to="/zones-livraison" className="hover:text-green-600">Zones de livraison</Link></li>
            <li><ChevronRight size={14} className="text-slate-400" /></li>
            <li className="font-medium text-slate-800" aria-current="page">{cityData.name}</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-green-200 mb-4">
              <MapPin size={18} />
              <span>{cityData.region} • À {cityData.distance} de notre dépôt</span>
            </div>
            <h1 data-testid="city-heading" className="text-4xl md:text-5xl font-black mb-6 break-words">
              {seo.h1}
            </h1>
            <p className="text-xl text-green-100 mb-8">
              {cityData.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors"
              >
                Demander un devis gratuit
                <ArrowRight size={20} />
              </Link>
              <a 
                href="tel:+33623893298"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-xl border-2 border-white hover:bg-green-400 transition-colors"
              >
                <Phone size={20} />
                06 23 89 32 98
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cityData.highlights.map((highlight, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0" size={24} />
                <span className="font-medium text-slate-700">{highlight}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services populaires */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-slate-800 mb-8">
                Services populaires {cityIn(cityData.name)}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {cityData.popularServices.map((service, index) => (
                  <ServiceCard key={index} service={service} />
                ))}
              </div>
              <Link 
                to="/univers"
                className="inline-flex items-center gap-2 text-green-600 font-semibold hover:text-green-700"
              >
                Voir tout notre catalogue
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="lg:sticky lg:top-6 lg:self-start">
              <QuickQuoteWidget
                defaultCity={cityData.name}
                title={`Votre devis ${cityIn(cityData.name)}`}
                subtitle="On vous rappelle sous 24h. Pas d'email requis."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nos univers */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-8">
            Nos Univers de Location
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/univers/evenementiel-barnum" className="group p-6 bg-orange-50 rounded-2xl border-2 border-orange-200 hover:border-orange-400 transition-all">
              <Tent className="text-orange-500 mb-4" size={32} />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Événementiel & Loisirs</h3>
              <p className="text-slate-600 text-sm">Chapiteaux, gonflables, mobilier, sono...</p>
            </Link>
            <Link to="/univers/bricolage-travaux" className="group p-6 bg-blue-50 rounded-2xl border-2 border-blue-200 hover:border-blue-400 transition-all">
              <Wrench className="text-blue-500 mb-4" size={32} />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Bricolage & Travaux</h3>
              <p className="text-slate-600 text-sm">Bétonnières, échafaudages, outillage...</p>
            </Link>
            <Link to="/univers/jardinage-motoculture" className="group p-6 bg-green-50 rounded-2xl border-2 border-green-200 hover:border-green-400 transition-all">
              <Leaf className="text-green-500 mb-4" size={32} />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Jardinage & Motoculture</h3>
              <p className="text-slate-600 text-sm">Motoculteurs, broyeurs, tronçonneuses...</p>
            </Link>
            <Link to="/univers/nettoyage-logement" className="group p-6 bg-purple-50 rounded-2xl border-2 border-purple-200 hover:border-purple-400 transition-all">
              <Sparkles className="text-purple-500 mb-4" size={32} />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Nettoyage & Logement</h3>
              <p className="text-slate-600 text-sm">Nettoyeurs, shampouineuses, aspirateurs...</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Contenu local SEO */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Votre partenaire location {cityIn(cityData.name)}
          </h2>
          <div className="prose prose-lg max-w-none text-slate-600">
            <p>{cityData.localContent}</p>
            <p className="mt-4">
              <strong>3DLoc</strong> est basé à <strong>Cauville-sur-Mer</strong>, à seulement <strong>{cityData.distance}</strong> {cityOf(cityData.name)}. 
              Notre proximité nous permet de vous offrir un service rapide et personnalisé, avec une connaissance approfondie 
              des besoins locaux et des spécificités de la région.
            </p>
          </div>
        </div>
      </section>

      {/* Villes à proximité */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">
            Nous livrons également à proximité {cityOf(cityData.name)}
          </h2>
          <div className="flex flex-wrap gap-3">
            {cityData.nearbyAreas.map((area, index) => (
              <span key={index} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                {area}
              </span>
            ))}
            <Link to="/zones-livraison" className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium hover:bg-green-200 transition-colors">
              Voir toutes nos zones →
            </Link>
          </div>
        </div>
      </section>

      {/* Avis clients */}
      <section className="py-16 bg-green-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="text-yellow-400 fill-yellow-400" size={24} />
            ))}
          </div>
          <p className="text-2xl font-bold text-slate-800 mb-2">Un loueur local noté par ses clients</p>
          <p className="text-slate-600 mb-4">
            Particuliers, artisans et collectivités nous font confiance autour {cityOf(cityData.name)} pour leurs chantiers, leur jardin et leurs événements.
          </p>
          <a
            href="https://www.google.com/search?q=3dloc+avis"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="city-google-reviews-link"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
          >
            Consulter les avis Google
          </a>
        </div>
      </section>

      {/* FAQ locale */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">
            Questions fréquentes - Location {cityIn(cityData.name)}
          </h2>
          <div className="space-y-4">
            {getCityFAQ(cityData).map((item, idx) => (
              <details
                key={idx}
                className="group bg-slate-50 rounded-xl border border-slate-200 overflow-hidden"
                data-testid={`city-faq-item-${idx}`}
              >
                <summary className="flex items-center justify-between cursor-pointer px-6 py-4 font-semibold text-slate-800 hover:bg-slate-100">
                  {item.question}
                  <ChevronRight className="transition-transform group-open:rotate-90" size={20} />
                </summary>
                <div className="px-6 pb-4 text-slate-600 leading-relaxed">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Besoin de matériel {cityIn(cityData.name)} ?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Demandez votre devis gratuit en moins de 2 minutes. Nous vous répondons sous 24h.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors text-lg"
            >
              Demander un devis gratuit
              <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityLandingPage;
